import { ALL_FEEDSTOCK_KEYS, FEEDSTOCK_KEY_TO_NAME, FeedstockName, MetricKey } from './emission-factor.constants';

import { normalizeNumericValue } from '../../../shared/utils/formatters.utils';
import { ProgressStatusColor } from '../../../shared/utils/progress.utils';

export interface AuditTrailEntry {
   updatedAt: string;
   updatedBy: string;
   comment?: string;
   value: number;
}

export interface AuditTrails {
   emission: AuditTrailEntry[];
   share: AuditTrailEntry[];
}

export interface FactorMetadata {
   efComment?: string | null;
   efUpdatedAt?: string | null;
   efUpdatedBy?: string | null;
   shareComment?: string | null;
   shareUpdatedAt?: string | null;
   shareUpdatedBy?: string | null;
   auditTrails?: AuditTrails;
}

export interface YearlyEmissionFactor extends FactorMetadata {
   year: number;
   emission: number;
   share: number;
}

export interface Feedstock {
   name: FeedstockName;
   factors: YearlyEmissionFactor[];
}

export interface ApiMaterialGrade {
   id: string;
   name?: string;
   groupId?: string;
   groupName?: string;
   feedstocks: Feedstock[];
}

export interface ApiSubtype {
   id: string;
   name?: string;
   grades: ApiMaterialGrade[];
}

export interface ApiMaterialType {
   id: string;
   name?: string;
   subtypes: ApiSubtype[];
}

export interface ApiEmissionFactor {
   id: string;
   name?: string;
   types: ApiMaterialType[];
}

interface ApiEmissionFactorWrapped {
   category: ApiEmissionFactor;
}

export interface EmissionFactorsApiResponse {
   emissionFactors: ApiEmissionFactorWrapped[];
}

export const getEmissionFactorCategories = (response: EmissionFactorsApiResponse): ApiEmissionFactor[] =>
   response.emissionFactors.map((item) => item.category);

export const getApiMaterialTypes = (category: ApiEmissionFactor): ApiMaterialType[] => category.types;

export const getApiSubtypes = (materialType: ApiMaterialType): ApiSubtype[] => materialType.subtypes;

export const getApiMaterialGrades = (subtype: ApiSubtype): ApiMaterialGrade[] => subtype.grades;

interface BaseHierarchyItem {
   id: string;
   displayName: string;
   progressText?: string;
   progressStatusColor?: ProgressStatusColor;
}

export interface MaterialCategory extends BaseHierarchyItem {
   materialTypes: MaterialType[];
}

export interface MaterialType extends BaseHierarchyItem {
   subtypes: MaterialSubtype[];
}

export interface MaterialSubtype extends BaseHierarchyItem {
   grades: MaterialGrade[];
}

export type MaterialGrade = BaseHierarchyItem;

export interface FeedstockYearlyValue {
   emission: number | null;
   share: number | null;
   metadata?: FactorMetadata;
}

export interface YearlyEmissionValues {
   year: number;
   renewable: FeedstockYearlyValue;
   recycled: FeedstockYearlyValue;
   nonRenewable: FeedstockYearlyValue;
}

export interface EmissionFactorRecord {
   gradeId: string;
   gradeName: string;
   categoryId: string;
   materialTypeId: string;
   subtypeId: string;
   yearlyValues: YearlyEmissionValues[];
   availableYears: number[];
   groupId?: string;
   groupName?: string;
}

export interface EmissionFactorFormState {
   selectedCategory: string | null;
   selectedMaterialType: string | null;
   selectedSubtype: string | null;
   selectedGrade: string | null;
   hasChanges: boolean;
}

export interface EmissionFactorChange {
   materialGradeId: string;
   feedstockName: FeedstockName;
   year: number;
   factorType: MetricKey;
   value: number;
   comment?: string;
   modifyAllInGroup: boolean;
}

export type UpdateEmissionFactorRequest = EmissionFactorChange[];

export interface UpdateEmissionFactorResponse {
   success: boolean;
}

export interface DeleteEmissionFactorYear {
   gradeId: string;
   year: number;
}

export interface DeleteEmissionFactorYearRequest {
   years: DeleteEmissionFactorYear[];
}

export interface EmissionFactorGroupsResponse {
   groups: Record<string, string[]>;
}

interface GradeTransformInput {
   id: string;
   name: string;
   categoryId: string;
   materialTypeId: string;
   subtypeId: string;
   feedstocks: Feedstock[];
   groupId?: string;
   groupName?: string;
}

export interface CategoryListItem {
   id: string;
   name?: string;
   progressCurrent?: number;
   progressTotal?: number;
}

function extractYearsFromFeedstocks(feedstocks: Feedstock[]): number[] {
   const yearsSet = new Set<number>();
   feedstocks.forEach((feedstock) => {
      feedstock.factors.forEach((factor) => {
         yearsSet.add(factor.year);
      });
   });
   return Array.from(yearsSet).sort((a, b) => a - b);
}

export function toEmissionFactorRecord(grade: GradeTransformInput): EmissionFactorRecord {
   const availableYears = extractYearsFromFeedstocks(grade.feedstocks);

   const findFeedstock = (name: FeedstockName) => grade.feedstocks.find((f) => f.name === name);
   const renewableFeedstock = findFeedstock('renewable');
   const recycledFeedstock = findFeedstock('recycled');
   const nonRenewableFeedstock = findFeedstock('non-renewable');

   const extractMetadata = (factor?: YearlyEmissionFactor): FactorMetadata | undefined => {
      if (!factor) return undefined;
      const { year, emission, share, ...metadata } = factor;
      return metadata;
   };

   const extractValue = (feedstock: Feedstock | undefined, year: number): FeedstockYearlyValue => {
      const factor = feedstock?.factors.find((f) => f.year === year);
      return {
         emission: factor?.emission ?? null,
         share: factor?.share ?? null,
         metadata: extractMetadata(factor),
      };
   };

   const yearlyValues: YearlyEmissionValues[] = availableYears.map((year) => ({
      year,
      renewable: extractValue(renewableFeedstock, year),
      recycled: extractValue(recycledFeedstock, year),
      nonRenewable: extractValue(nonRenewableFeedstock, year),
   }));

   return {
      gradeId: grade.id,
      gradeName: grade.name,
      categoryId: grade.categoryId,
      materialTypeId: grade.materialTypeId,
      subtypeId: grade.subtypeId,
      yearlyValues,
      availableYears,
      groupId: grade.groupId,
      groupName: grade.groupName,
   };
}

export function toUpdateRequest(
   original: EmissionFactorRecord,
   current: EmissionFactorRecord,
   comments?: Record<string, string>,
   modifyAllInGroup = false
): UpdateEmissionFactorRequest {
   const changes: EmissionFactorChange[] = [];
   const fields: MetricKey[] = ['emission', 'share'];
   const originalByYear = new Map(original.yearlyValues.map((yv) => [yv.year, yv]));

   current.yearlyValues.forEach((currentYV) => {
      const originalYV = originalByYear.get(currentYV.year);

      ALL_FEEDSTOCK_KEYS.forEach((feedstockKey) => {
         const currentFeedstock = currentYV[feedstockKey];
         const originalFeedstock = originalYV?.[feedstockKey];

         fields.forEach((field) => {
            const oldValue = originalFeedstock?.[field] ?? null;
            const newValue = currentFeedstock?.[field] ?? null;

            if (normalizeNumericValue(oldValue) !== normalizeNumericValue(newValue)) {
               const commentKey = `${currentYV.year}-${feedstockKey}-${field}`;
               const comment = comments?.[commentKey];

               changes.push({
                  materialGradeId: current.gradeId,
                  feedstockName: FEEDSTOCK_KEY_TO_NAME[feedstockKey],
                  year: currentYV.year,
                  factorType: field,
                  value: normalizeNumericValue(newValue),
                  modifyAllInGroup,
                  ...(comment && { comment }),
               });
            }
         });
      });
   });

   return changes;
}

export function getRemovedYears(original: EmissionFactorRecord, current: EmissionFactorRecord): number[] {
   const currentYears = new Set(current.availableYears);
   return original.availableYears.filter((year) => !currentYears.has(year));
}
